import type {
  RunbookExecutionRecord,
  RunbookExecutionSummaryRecord,
} from "@bitsentry-ce/components/services";
import { queryClient } from "./client";
import { dashboardKeys, type ExecutionList } from "./dashboard";

export function toExecutionSummary(
  execution: RunbookExecutionRecord,
): RunbookExecutionSummaryRecord {
  return {
    executionId: execution.executionId,
    runbookId: execution.runbookId,
    runbookTitle: execution.runbookTitle,
    status: execution.status,
    snapshotVersion: execution.snapshotVersion ?? 0,
    startedAt: execution.startedAt,
    completedAt: execution.completedAt,
    stepCount: execution.steps.length,
    completedStepCount: execution.steps.filter(
      (step) => step.status === "completed",
    ).length,
  };
}

export function executionSnapshotVersion(
  execution:
    | Pick<RunbookExecutionRecord, "snapshotVersion">
    | Pick<RunbookExecutionSummaryRecord, "snapshotVersion">,
): number {
  if (typeof execution.snapshotVersion === "number") {
    return execution.snapshotVersion;
  }

  return 0;
}

function shouldApplySummaryUpdate(
  current: RunbookExecutionSummaryRecord,
  next: RunbookExecutionSummaryRecord,
): boolean {
  return executionSnapshotVersion(next) >= executionSnapshotVersion(current);
}

function mergeResultAtIndex(
  result: RunbookExecutionSummaryRecord,
  index: number,
  existingIndex: number,
  nextResult: RunbookExecutionSummaryRecord,
) {
  if (index === existingIndex) {
    return { ...result, ...nextResult };
  }

  return result;
}

export function upsertResult(
  list: RunbookExecutionSummaryRecord[],
  nextResult: RunbookExecutionSummaryRecord,
): RunbookExecutionSummaryRecord[] {
  const existingIndex = list.findIndex(
    (result) => result.executionId === nextResult.executionId,
  );

  if (existingIndex === -1) {
    return [nextResult, ...list];
  }

  if (!shouldApplySummaryUpdate(list[existingIndex], nextResult)) {
    return list;
  }

  return list.map((result, index) =>
    mergeResultAtIndex(result, index, existingIndex, nextResult),
  );
}

export function cacheExecution(
  scope: string,
  execution: RunbookExecutionRecord,
): void {
  const summary = toExecutionSummary(execution);
  for (const [key, previous] of queryClient.getQueriesData<ExecutionList>({
    queryKey: dashboardKeys.executions(scope),
  })) {
    if (!previous) continue;
    const filters = (key[4] ?? {}) as { runbookId?: string };
    if (filters.runbookId && filters.runbookId !== execution.runbookId)
      continue;
    const exists = previous.executions.some(
      (item) => item.executionId === execution.executionId,
    );
    const oldest = previous.executions[previous.executions.length - 1];
    // A delayed event for an execution outside this page is not a new run.
    if (
      !exists &&
      previous.executions.length >= 100 &&
      oldest &&
      summary.startedAt <= oldest.startedAt
    )
      continue;
    const next = upsertResult(previous.executions, summary);
    if (next === previous.executions) continue;
    const executions = [...next]
      .sort((left, right) => right.startedAt.localeCompare(left.startedAt))
      .slice(0, 100);
    queryClient.setQueryData<ExecutionList>(key, {
      ...previous,
      executions,
      total: previous.total + (exists ? 0 : 1),
      hasMore:
        previous.hasMore || previous.executions.length + (exists ? 0 : 1) > 100,
    });
  }
}

export function mergeFetchedExecutions(
  scope: string,
  runbookId: string | undefined,
  response: ExecutionList,
  initialExecutionIds: ReadonlySet<string> = new Set(),
): ExecutionList {
  const cached = queryClient.getQueryData<ExecutionList>(
    dashboardKeys.executionList(scope, runbookId),
  );
  if (!cached) return response;
  const byId = new Map(
    cached.executions.map((execution) => [execution.executionId, execution]),
  );
  const executions = response.executions.map((execution) => {
    const current = byId.get(execution.executionId);
    return current &&
      executionSnapshotVersion(current) > executionSnapshotVersion(execution)
      ? current
      : execution;
  });
  const responseIds = new Set(
    executions.map((execution) => execution.executionId),
  );
  const arrivedDuringFetch = cached.executions.filter(
    (execution) =>
      !initialExecutionIds.has(execution.executionId) &&
      !responseIds.has(execution.executionId),
  );
  return {
    ...response,
    total: response.total + arrivedDuringFetch.length,
    hasMore:
      response.hasMore || executions.length + arrivedDuringFetch.length > 100,
    executions: [...arrivedDuringFetch, ...executions]
      .sort((left, right) => right.startedAt.localeCompare(left.startedAt))
      .slice(0, 100),
  };
}
